/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import React, { ReactNode } from 'react';
import {
  Spread,
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  EditorConfig,
  LexicalNode,
  SerializedLexicalNode,
  DecoratorNode,
  $applyNodeReplacement,
  NodeKey,
} from 'lexical';

// const GenericComponent = React.lazy(
//   () => import('./components/LexicalGenericComponent/LexicalGenericComponent'),
// );

// import GenericComponent from './components/LexicalGenericComponent/LexicalGenericComponent';

export type GenericComponentType = 'inline-subscribe' | 'custom-button' | 'divider';

export type GenericComponentCustomButtonData = {
  text: string;
  url: string;
};

export type SerializedGenericComponentNode = Spread<
  {
    componentType: GenericComponentType;
    data?: any | GenericComponentCustomButtonData;
  },
  SerializedLexicalNode
>;

function convertGenericComponentElement(domNode: HTMLElement): DOMConversionOutput | null {
  const componentType = domNode.getAttribute(
    'data-lexical-generic-component-type',
  ) as GenericComponentType;

  if (componentType !== null) {
    const node = $createGenericComponentNode(componentType);
    return {
      node,
    };
  }

  return null;
}

const TYPE = 'generic-component';

export class GenericComponentNode extends DecoratorNode<ReactNode> {
  __componentType: GenericComponentType;
  __data: any;

  static getType(): string {
    return TYPE;
  }

  static clone(node: GenericComponentNode): GenericComponentNode {
    return new GenericComponentNode(node.__componentType, node.__data, node.__key);
  }

  constructor(componentType: GenericComponentType, data?: any, key?: NodeKey) {
    super(key);
    this.__componentType = componentType;
    this.__data = data;
  }

  isInline(): boolean {
    return true;
  }

  createDOM(_config: EditorConfig): HTMLElement {
    const dom = document.createElement('div');
    dom.className = 'componentType';
    return dom;
  }

  updateDOM(): false {
    return false;
  }

  static importJSON(serializedNode: SerializedGenericComponentNode): GenericComponentNode {
    const node = $createGenericComponentNode(serializedNode.componentType, serializedNode.data);
    return node;
  }

  exportJSON(): SerializedGenericComponentNode {
    return {
      componentType: this.__componentType,
      data: this.getData(),
      type: TYPE,
      version: 1,
    };
  }

  getTextContent(
    _includeInert?: boolean | undefined,
    _includeDirectionless?: false | undefined,
  ): string {
    return this.__data?.text;
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('img');
    element.setAttribute('src', this.__componentType);
    element.setAttribute('data-lexical-generic-component-type', this.__componentType);
    return { element };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      img: (domNode: HTMLElement) => {
        if (!domNode.hasAttribute('data-lexical-generic-component-type')) {
          return null;
        }
        return {
          conversion: convertGenericComponentElement,
          priority: 1,
        };
      },
    };
  }

  isTextEntity(): true {
    return true;
  }

  canInsertTextBefore(): boolean {
    return false;
  }

  canInsertTextAfter(): boolean {
    return false;
  }

  setComponentType(componentType: GenericComponentType): void {
    const self = this.getWritable();
    self.__componentType = componentType;
  }

  setData(data: any): void {
    const self = this.getWritable();
    self.__data = data;
  }

  getData(): any {
    const self = this.getLatest();
    return self.__data;
  }

  decorate(): ReactNode {
    return null;
  }

  // decorate(): ReactNode {
  //   if (typeof window === 'undefined') {
  //     return null;
  //   }

  //   return (
  //     <GenericComponent
  //       componentType={this.__componentType}
  //       nodeKey={this.__key}
  //       data={this.__data}
  //       setComponentType={(componentType) => {
  //         this.setComponentType(componentType);
  //       }}
  //       setData={(data) => {
  //         this.setData(data);
  //       }}
  //     />
  //   );
  // }
}

export function $createGenericComponentNode(
  componentType: GenericComponentType,
  data?: any,
): GenericComponentNode {
  return new GenericComponentNode(componentType, data);
}

export function $isGenericComponentNode(
  node: LexicalNode | null | undefined,
): node is GenericComponentNode {
  return node instanceof GenericComponentNode;
}
